// frontend/src/rma/lib/lsmUrls.js
//
// Centralized constructors for LSM portal deep links.
//
// Why this exists: links built ad-hoc with `${BASE}?search=true&target=...&value1=...&submit=true`
// were missing `ctrlType=viewWarehouseOrderHeader` + `currentUrlFileName=...`. When opened
// in a browser tab that already had an authenticated LSM session with prior controller
// state, the missing params didn't matter — the existing session carried them. But in
// the POG mobile PWA's webview (and any cold session), LSM bounced the request with
// "Error: Controller is not set!" because the controller wasn't dispatched correctly.
//
// All LSM links across the app should now go through one of these helpers.

const PORTAL_BASE = "https://control.lsm-fulfillment.de"

function _orderUrl(orderType, number) {
  // orderType is the LSM section path: "returnorder" (RT), "outboundorder" (DN/AVIS-out),
  // "inboundorder" (DN/AVIS-in). The same query-string template works for all three.
  const params = new URLSearchParams({
    search: "true",
    target: "formWarehouseOrderHeaderView",
    field1: "warehouseOrderHeaderNumber",
    value1: number || "",
    submit: "true",
    currentUrlFileName: orderType,
    ctrlType: "viewWarehouseOrderHeader",
  })
  return `${PORTAL_BASE}/system/control/${orderType}?${params.toString()}`
}

/** Deep link to a return (RT-…) in the LSM portal. */
export function lsmReturnUrl(rtNumber) {
  return _orderUrl("returnorder", rtNumber)
}

/** Deep link to an outbound delivery (DN-…) in the LSM portal. */
export function lsmDeliveryUrl(dnNumber) {
  return _orderUrl("outboundorder", dnNumber)
}

/** Deep link to an inbound delivery (AVIS, etc.) in the LSM portal. */
export function lsmInboundUrl(refNumber) {
  return _orderUrl("inboundorder", refNumber)
}

/** Generic landing page on the inbound-entry section. No specific order. */
export function lsmInboundEntryUrl() {
  return `${PORTAL_BASE}/system/control/inboundentry`
}

/** Generic landing page on the outbound-entry section. No specific order. */
export function lsmOutboundEntryUrl() {
  return `${PORTAL_BASE}/system/control/outboundentry`
}

/**
 * Deep link to an article in the LSM portal, searched by SKU.
 *
 * CONFIRMED WORKING (2026-06-17): the generated URL resolves correctly to the
 * article view in the LSM portal — verified by the user. Mirrors the `_orderUrl`
 * search template, targeting the `article` section and searching by article
 * number, with `ctrlType: "viewArticle"`. (Was previously flagged R1/unconfirmed
 * — that caveat is now lifted.)
 *
 * @param {string} sku - the article number (POG SKU == LSM article number).
 * @returns {string} a search URL on the LSM article section.
 */
export function lsmArticleSearchUrl(sku) {
  const params = new URLSearchParams({
    search: "true",
    target: "formArticleView",
    field1: "articleNumber",
    value1: sku || "",
    submit: "true",
    currentUrlFileName: "article",
    ctrlType: "viewArticle", // confirmed working 2026-06-17
  })
  return `${PORTAL_BASE}/system/control/article?${params.toString()}`
}
